import React from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { AUTOMATION_TEMPLATES, getTemplateIcon, getCategoryColor, getFrequencyLabel, AutomationTemplateData } from './automationHelpers';

interface AutomationTemplatesProps {
  onUseTemplate: (template: AutomationTemplateData) => void;
  isModal?: boolean;
  onClose?: () => void;
}

const AutomationTemplates: React.FC<AutomationTemplatesProps> = ({ onUseTemplate, isModal = false, onClose }) => {
  const { t } = useTranslation();

  const content = (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {AUTOMATION_TEMPLATES.map((template) => {
        const Icon = getTemplateIcon(template.iconName);
        const colors = getCategoryColor(template.category);
        return (
          <div key={template.id} className="p-4 border-2 border-gray-200 dark:border-gray-700 rounded-xl hover:border-primary-400 dark:hover:border-primary-600 cursor-pointer transition-all group" onClick={() => onUseTemplate(template)}>
            <div className="flex items-start gap-3">
              <div className={`p-3 rounded-xl ${colors.bg}`}>
                <Icon className={`w-5 h-5 ${colors.text}`} />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="font-semibold text-gray-900 dark:text-white">{template.name}</h3>
                  <span className="text-xs text-gray-500 dark:text-gray-400">{template.popularity}%</span>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">{template.description}</p>
                <div className="flex items-center gap-2 text-xs">
                  {template.platform.map((p: string) => (
                    <span key={p} className={`px-2 py-1 rounded-full ${p === 'google' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300' : p === 'meta' ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300' : 'bg-sky-100 text-sky-700 dark:bg-sky-900/50 dark:text-sky-300'}`}>
                      {p === 'google' ? 'Google' : p === 'meta' ? 'Meta' : 'LinkedIn'}
                    </span>
                  ))}
                  <span className="text-gray-400">{getFrequencyLabel(template.frequency)}</span>
                </div>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 opacity-0 group-hover:opacity-100 transition-opacity">
              <button className="w-full btn-primary btn-sm">{t('automation.useTemplate', 'Use template')}</button>
            </div>
          </div>
        );
      })}
    </div>
  );

  if (isModal && onClose) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
          <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{t('automation.templates.title', 'Templates')}</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{t('automation.templates.subtitle', 'Pre-built automation rules')}</p>
            </div>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          <div className="p-6 overflow-y-auto">{content}</div>
        </div>
      </div>
    );
  }

  return content;
};

export default AutomationTemplates;
